import { NgClass } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IMAGE_SUPPORTED_TYPES, MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_UNITS } from 'shared/constants/general-constants';
import { IUploadPreviewSettings, IUploadPreviewSettingsXLSX } from 'shared/interfaces/upload-preview-setting.interface';
import { ToastService } from 'shared/services';
import { checkFileSize } from 'shared/util/check-file-size';

const maxSizeError =
    "When giving a max file size please make sure it's a valid number lower than 1024 followed by a unit (KB, MB, GB, TB).";

@Component({
    selector: 'app-file-upload',
    templateUrl: './file-upload.component.html',
    styleUrls: ['./file-upload.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FileUploadComponent,
            multi: true,
        },
    ],
    imports: [NgClass, TranslateModule],
})
export class FileUploadComponent implements ControlValueAccessor {
    @ViewChild('attachments') attachments: any;

    @Input() settings!: IUploadPreviewSettings | IUploadPreviewSettingsXLSX;
    @Input() configuration?: {
        disableDeleteButtonOnEdit?: boolean;
        suppressFileNameOnEdit?: boolean;
    } = {
        disableDeleteButtonOnEdit: false,
        suppressFileNameOnEdit: false,
    };

    @Input() set maxFileSize(value: string | undefined) {
        if (!value) {
            this.maxFileSizeObject = undefined;
            return;
        }

        const matchedValue = value.match(/^(\d*(\.\d+)?)\s*([kKmMgGtT]?[bB])/);

        if (!matchedValue) {
            throw new TypeError(maxSizeError);
        }

        const convertedValue = Number(matchedValue[1]);
        const convertedUnit = matchedValue[3].toUpperCase();

        if (Number.isNaN(convertedValue)) {
            throw new TypeError(maxSizeError);
        }

        this.maxFileSizeObject = {
            value: convertedValue,
            unit: convertedUnit,
        };
    }

    get maxFileSize(): string | undefined {
        return this.maxFileSizeObject ? this.maxFileSizeObject.value.toString(10) + ' ' + this.maxFileSizeObject.unit : undefined;
    }

    @Output() fileChangedEvent = new EventEmitter<File[]>();

    public isImage!: boolean | undefined;
    public documentFileNames: string[] = [];
    public files: File[] = [];
    public fileUrls: (string | ArrayBuffer)[] = [];
    public guid!: string;
    public isOutsideFile!: boolean;

    private maxFileSizeObject?: { unit: string; value: number };
    private onChangeFunction!: Function;
    private onTouchFunction!: Function;

    constructor(
        private toastService: ToastService,
        private translateService: TranslateService,
        private host: ElementRef<HTMLInputElement>,
    ) {
        this.guid = (crypto as any).randomUUID();
    }

    public registerOnChange(fn: any): void {
        this.onChangeFunction = fn;
    }

    public registerOnTouched(fn: any): void {
        this.onTouchFunction = fn;
    }

    public writeValue(value: string | string[] | null) {
        if (!value) {
            // clear file input
            this.host.nativeElement.value = '';
            this.files = [];
            this.isOutsideFile = false;
        } else {
            this.isOutsideFile = true;

            if (!Array.isArray(value)) {
                value = [value];
            }

            for (let i = 0; i < value.length; i++) {
                const currentValue = value[i];
                this.isImage = Boolean(
                    IMAGE_SUPPORTED_TYPES.split(', ').find((extension) => {
                        return currentValue.toLowerCase().endsWith(extension.toLowerCase());
                    }),
                );

                this.fileUrls[i] = currentValue;
                this.documentFileNames[i] = this.getFileName(currentValue);
            }
        }
    }

    public setDisabledState(isDisabled: boolean): void {}

    public selectFiles(event: Event) {
        const files = (event.target as HTMLInputElement).files;
        this.files = [];
        this.isOutsideFile = false;

        if (files && files instanceof FileList && files.length) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                const mimeType = file.type;
                const fileExtension = file.name.split('.').pop();

                if (
                    mimeType.match(this.settings.accept?.replaceAll(', ', '|')) == null &&
                    (!fileExtension || !this.settings.accept.includes(fileExtension))
                ) {
                    this.toastService.showToast('generalKeywords.fileUpload.typeNotSupported', 'error', {
                        type: this.settings.extensions,
                    });
                    this.clearInput();
                    continue;
                }

                const fileCheck = checkFileSize(file.size, this.maxFileSizeObject);

                if (!fileCheck.acceptedFileSize) {
                    let maxValue = MAX_IMAGE_SIZE;
                    let maxUnit = MAX_IMAGE_SIZE_UNITS;

                    if (this.maxFileSizeObject) {
                        maxValue = this.maxFileSizeObject.value;
                        maxUnit = this.maxFileSizeObject.unit;
                    }

                    this.toastService.showToast('generalKeywords.fileUpload.sizeExceeded', 'error', {
                        maxSize: maxValue,
                        unit: maxUnit,
                        fileName: file.name,
                    });
                    // this.clearInput();
                    continue;
                }

                this.files.push(file);
                this.documentFileNames.push(file.name);
            }

            if (this.files.length) {
                this.onTouchFunction();
                this.onChangeFunction(this.files);
                this.fileChangedEvent.emit(this.files);
                this.previewFiles();
            }
        } else {
            this.toastService.showToast('generalKeywords.fileUpload.noFileSelected', 'error');
            this.clearInput();
            return;
        }
    }

    clearInput(index?: number) {
        if (index === undefined) {
            if (this.attachments && this.attachments.nativeElement) {
                this.attachments.nativeElement.value = '';
            }

            this.files = [];
            this.fileUrls = [];
            this.documentFileNames = [];
            this.host.nativeElement.value = '';
        } else {
            if (this.files.length) {
                this.files.splice(index, 1);
            }

            this.fileUrls.splice(index, 1);
            this.documentFileNames.splice(index, 1);

            if (!this.files.length) {
                this.attachments.nativeElement.value = '';
            }
        }
        this.onTouchFunction();
        this.onChangeFunction(this.files);
        this.fileChangedEvent.emit(this.files);
    }

    previewFiles(): void {
        if (!Array.isArray(this.files) || !this.files.length) {
            return;
        }

        if (this.settings.fileType === 'image') {
            for (let i = 0; i < this.files.length; i++) {
                const file = this.files[i];

                // If file is bigger than 25 MB...
                // We don't want to crash the browser
                if (file.size < 26_214_400) {
                    const reader = new FileReader();

                    reader.readAsDataURL(file);
                    reader.onload = (_event) => {
                        if (reader.result) {
                            this.fileUrls[i] = reader.result;
                        }
                    };
                }
            }
        } else {
            for (let i = 0; i < this.files.length; i++) {
                const file = this.files[i];

                this.fileUrls[i] = file.name;
                this.documentFileNames[i] = this.getFileName(file.name);
            }
        }
    }

    getFileName(url: string): string {
        const lastIndex = url.lastIndexOf('/');
        return url.substring(lastIndex + 1);
    }
}
